/**
 * AssetChip — render a covered-asset reference as "Name (DUID)".
 *
 * Used in three places (Term Sheet, Contracts dashboard table, ContractDetail
 * meta strip) so the styling is consistent everywhere AND survives dark/light
 * mode without contrast bugs. Earlier hand-rolled inline chips had a
 * white-on-white issue in dark mode because the inner spans didn't inherit
 * the chip's color through MUI's `MuiChip-label` class.
 *
 * Props
 * ─────
 *   asset:    {asset_id, name?, …}  — `name` optional (falls back to DUID)
 *   compact:  if true, smaller font + heights (for table rows)
 *
 * Theme: uses `primary.main` for accent (works in both modes because the
 * theme palette adapts) and `.MuiChip-label` color overrides at the sx level
 * so the inner spans inherit correctly.
 */

import React from 'react';
import { Chip, Tooltip } from '@mui/material';

const AssetChip = ({ asset, compact = false }) => {
  if (!asset) return null;
  const { asset_id, name } = asset;

  const label = name ? (
    <span style={{ color: 'inherit' }}>
      {name}
      <span
        style={{
          color: 'inherit',
          opacity: 0.65,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '0.85em',
          marginLeft: 4,
        }}
      >
        ({asset_id})
      </span>
    </span>
  ) : (
    asset_id
  );

  const chip = (
    <Chip
      label={label}
      size="small"
      sx={{
        // Use theme tokens that adapt to light/dark mode automatically. The
        // chip's bgcolor is a faint tint of primary; outline is a denser tint
        // — both adapt because they alpha-blend onto whatever the underlying
        // surface is.
        bgcolor: (theme) =>
          theme.palette.mode === 'dark'
            ? 'rgba(217, 138, 43, 0.14)'
            : 'rgba(217, 138, 43, 0.10)',
        border: '1px solid',
        borderColor: 'primary.main',
        color: 'primary.main',
        fontWeight: 600,
        fontSize: compact ? '0.7rem' : '0.78rem',
        height: compact ? 22 : 'auto',
        py: compact ? 0 : 0.5,
        maxWidth: compact ? 200 : 'none',
        // Force the inner label to use the chip's color too — otherwise the
        // <span> inherits default text colour (white in dark, dark in light)
        // and gives the "name white / brackets coloured" mismatch.
        '& .MuiChip-label': {
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          gap: 0,
        },
      }}
    />
  );

  return name ? <Tooltip title={`${name} (${asset_id})`}>{chip}</Tooltip> : chip;
};

export default AssetChip;
